import { useState, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { DailySunlightResult } from '@/types/sunlight';
import { isPointInPolygon2D } from '@/utils/rayTracing';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { WorkerOutput } from '@/workers/sunlight.worker';
import { getShadowCastersForWorker, SerializedMesh } from '@/utils/meshSerialization';
import {
	initializeSunlightVisualization,
	updateSunlightVisualization,
	changeSunlightVisualizationMetric,
	updateSelectedRegionVisualization,
	initializeSelectedRegionVisualization,
	changeSelectedRegionVisualizationMetric,
	clearAllVisualizations,
	createPolygonFromGridPoints,
	persistPolygonFill,
} from '@/utils/visualization';
import { clearAllSelections, getSelectedGridPoints } from '@/utils/gridSelection';
import { buildDateSequence } from '@/utils/dateSequence';
import { calculateETA } from '@/utils/etaCalculation';

interface GridData {
	allGridPoints: Array<{ x: number; y: number; z: number }>;
	roomNames: string[];
	roomUnitIdMap: Map<string, string | undefined>;
}

/**
 * Generate grid points from room polygons
 */
const generateRoomGridPoints = (
	rooms: THREE.Object3D,
	gridSize: number,
	selectedFloorIndex: number | null = null
): GridData => {
	const allGridPoints: Array<{ x: number; y: number; z: number }> = [];
	const roomNames: string[] = [];
	const roomUnitIdMap = new Map<string, string | undefined>();

	for (let i = 0; i < rooms.children.length; i++) {
		const room = rooms.children[i];
		const roomFloorIndex =
			typeof room.userData.floorIndex === 'number' ? room.userData.floorIndex : null;
		if (selectedFloorIndex !== null && roomFloorIndex !== selectedFloorIndex) {
			continue;
		}
		const basePolygon: { x: number; y: number }[] | undefined = room.userData.basePolygon;

		if (!basePolygon || !Array.isArray(basePolygon) || basePolygon.length < 3) continue;

		let minX = Infinity,
			maxX = -Infinity,
			minY = Infinity,
			maxY = -Infinity;
		basePolygon.forEach((p) => {
			if (p.x < minX) minX = p.x;
			if (p.x > maxX) maxX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.y > maxY) maxY = p.y;
		});

		const roomWidth = maxX - minX;
		const roomDepth = maxY - minY;
		const cellsX = Math.max(1, Math.ceil(roomWidth / gridSize));
		const cellsY = Math.max(1, Math.ceil(roomDepth / gridSize));
		const cellWidth = roomWidth / cellsX;
		const cellDepth = roomDepth / cellsY;

		const bbox = new THREE.Box3().setFromObject(room);
		const measurementZ = bbox.min.z + 0.1; // Z-up: 0.1m above floor

		for (let x = 0; x < cellsX; x++) {
			for (let y = 0; y < cellsY; y++) {
				const localCenterX = minX + (x + 0.5) * cellWidth;
				const localCenterY = minY + (y + 0.5) * cellDepth;
				const localGridPoint = new THREE.Vector3(localCenterX, localCenterY, 0);

				const localPointXY = new THREE.Vector2(localCenterX, localCenterY);
				const polygonLocal = basePolygon.map((p) => new THREE.Vector2(p.x, p.y));

				if (!isPointInPolygon2D(localPointXY, polygonLocal)) continue;

				const worldGridPoint = localGridPoint.applyMatrix4(room.matrixWorld);
				worldGridPoint.z = measurementZ;

				allGridPoints.push({
					x: worldGridPoint.x,
					y: worldGridPoint.y,
					z: worldGridPoint.z,
				});
				roomNames.push(room.name);
			}
		}
	}

	// Build room name to unit ID mapping
	for (let i = 0; i < rooms.children.length; i++) {
		const room = rooms.children[i];
		const roomFloorIndex =
			typeof room.userData.floorIndex === 'number' ? room.userData.floorIndex : null;
		if (selectedFloorIndex !== null && roomFloorIndex !== selectedFloorIndex) {
			continue;
		}
		roomUnitIdMap.set(room.name, room.userData.unit?.id);
	}

	return { allGridPoints, roomNames, roomUnitIdMap };
};

export interface DailySunlightHook {
	calculatingDailySunlight: boolean;
	calculateDailySunlightForRooms: (
		selectedFloorIndex?: number | null
	) => Promise<DailySunlightResult[] | undefined>;
	calculateDailySunlightForSelectedGridPoints: () => Promise<DailySunlightResult[] | undefined>;
	changeSunlightVisualizationMetric: (metric: keyof DailySunlightResult) => void;
	clearAllVisualizations: () => void;
	startDailyAnimation: () => Promise<void>;
	stopDailyAnimation: () => void;
	isAnimating: boolean;
	calculateSingleDay: (dayIndex: number) => Promise<void>;
}

export interface DailySunlightParams {
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	starsRef: React.MutableRefObject<THREE.Points | null>;
	latitude: number | null;
	longitude: number | null;
}

export const useDailySunlight = (params: DailySunlightParams): DailySunlightHook => {
	const { sceneRef, latitude, longitude } = params;

	const [calculatingDailySunlight, setCalculatingDailySunlight] = useState(false);
	const setCurrentStatusMessage = useDailySunlightStore((state) => state.setCurrentStatusMessage);
	const accumulatedResultsRef = useRef<DailySunlightResult[]>([]);
	const accumulatedRegionResultsRef = useRef<DailySunlightResult[]>([]);
	const animationControlRef = useRef<{ shouldStop: boolean }>({ shouldStop: false });
	const isAnimating = useDailySunlightStore((state) => state.isAnimatingDaily);
	const setIsAnimatingDaily = useDailySunlightStore((state) => state.setIsAnimatingDaily);
	const setCurrentDailyAnimationDay = useDailySunlightStore((state) => state.setCurrentDailyAnimationDay);

	const runSunlightWorker = async (
		gridPoints: Array<{ x: number; y: number; z: number }>,
		shadowGeometry: SerializedMesh[],
		analysisDates: string[],
		timeStepMinutes: number,
		onProgress: (results: DailySunlightResult[]) => void,
		onComplete: (results: DailySunlightResult[]) => void
	): Promise<void> => {
		return new Promise((resolve, reject) => {
			const calculationWorker = new Worker(
				new URL('../workers/sunlight.worker.ts', import.meta.url),
				{ type: 'module' }
			);

			const workerPayload = {
				gridPoints, // Z-up: (0,0,1) is up
				shadowCasters: shadowGeometry,
				dates: analysisDates,
				latitude,
				longitude,
				timeStepMinutes,
				workerIndex: 0,
			};

			calculationWorker.onmessage = (msg) => {
				const msgData = msg.data;

				if (msgData.type === 'progress') {
					const progressBatch = msgData.partialResults || [];

					if (progressBatch.length > 0) {
						const formattedBatch = progressBatch.map(
							(item: WorkerOutput['results'][0]) => ({
								...item,
								point: new THREE.Vector3(
									gridPoints[item.pointIndex].x,
									gridPoints[item.pointIndex].y,
									gridPoints[item.pointIndex].z
								),
							})
						);

						onProgress(formattedBatch);
					}
				} else if (msgData.type === 'complete') {
					const completeResults = msgData.results.map(
						(item: WorkerOutput['results'][0]) => ({
							...item,
							point: new THREE.Vector3(
								gridPoints[item.pointIndex].x,
								gridPoints[item.pointIndex].y,
								gridPoints[item.pointIndex].z
							),
						})
					);

					onComplete(completeResults);
					calculationWorker.terminate();
					resolve();
				}
			};

			calculationWorker.onerror = (err) => {
				console.error('Worker error:', err);
				calculationWorker.terminate();
				reject(err);
			};

			calculationWorker.postMessage(workerPayload);
		});
	};

	const runSunlightWorkers = async (
		gridPoints: Array<{ x: number; y: number; z: number }>,
		roomNames: string[],
		roomUnitIdMap: Map<string, string | undefined>,
		shadowGeometry: SerializedMesh[],
		analysisDates: string[],
		timeStepMinutes: number,
		numWorkers: number,
		onProgress: (
			results: DailySunlightResult[],
			workersCompleted: number,
			totalWorkers: number
		) => void,
		onComplete: (results: DailySunlightResult[]) => void
	): Promise<void> => {
		const allResults: DailySunlightResult[] = [];
		const workerPromises: Promise<void>[] = [];
		let completedWorkers = 0;
		const workerPointIndices: number[][] = Array.from({ length: numWorkers }, () => []);
		
		// Distribute points in round-robin fashion: worker 0 gets 0,4,8..., worker 1 gets 1,5,9...
		gridPoints.forEach((_, index) => {
			const workerIdx = index % numWorkers;
			workerPointIndices[workerIdx].push(index);
		});

		for (let workerIdx = 0; workerIdx < numWorkers; workerIdx++) {
			const indices = workerPointIndices[workerIdx];
			if (indices.length === 0) continue;
			
			const workerGridPoints = indices.map(idx => gridPoints[idx]);
			const workerRoomNames = indices.map(idx => roomNames[idx]);

			const workerPromise = runSunlightWorker(
				workerGridPoints,
				shadowGeometry,
				analysisDates,
				timeStepMinutes,
				(progressResults) => {
					const mappedResults = progressResults.map((r) => {
						const roomName = workerRoomNames[r.pointIndex];
						return {
							...r,
							pointIndex: indices[r.pointIndex],
							roomName,
							unitId: roomUnitIdMap.get(roomName),
						};
					});
					onProgress(mappedResults, completedWorkers, numWorkers);
				},
				(completeResults) => {
					const mappedResults = completeResults.map((r) => {
						const roomName = workerRoomNames[r.pointIndex];
						return {
							...r,
							pointIndex: indices[r.pointIndex],
							roomName,
							unitId: roomUnitIdMap.get(roomName),
						};
					});
					allResults.push(...mappedResults);
					completedWorkers++;
				}
			);

			workerPromises.push(workerPromise);
		}

		await Promise.all(workerPromises);
		onComplete(allResults);
	};

	const calculateDailySunlightForRooms = async (
		selectedFloorIndex: number | null = null
	): Promise<DailySunlightResult[] | undefined> => {
		const rooms = sceneRef.current?.getObjectByName('RoomFloorPolygonsGroup');
		if (!rooms || latitude === null || longitude === null || !sceneRef.current) return;

		setCalculatingDailySunlight(true);
		accumulatedResultsRef.current = [];

		const gridSize = useDailySunlightStore.getState().gridSize;
		const startDate = useDailySunlightStore.getState().startDate;
		const endDate = useDailySunlightStore.getState().endDate;
		const useApproximation = useDailySunlightStore.getState().useApproximation;

		if (!startDate || !endDate) {
			console.error('Start date or end date is not set');
			setCalculatingDailySunlight(false);
			return;
		}

		try {
			const shadowCasters = getShadowCastersForWorker(sceneRef.current);
			const dates = buildDateSequence(startDate, endDate, useApproximation);
			const { allGridPoints, roomNames, roomUnitIdMap } = generateRoomGridPoints(
				rooms,
				gridSize,
				selectedFloorIndex
			);

			setCurrentStatusMessage('Generating room grid points...');

			initializeSunlightVisualization(sceneRef.current, allGridPoints.length, gridSize);

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);

			setCurrentStatusMessage(
				`Calculating sunlight for ${allGridPoints.length} room points...`
			);

			const startTime = Date.now();
			let lastUpdateTime = startTime;
			let lastEtaText = '';

			await runSunlightWorkers(
				allGridPoints,
				roomNames,
				roomUnitIdMap,
				shadowCasters,
				dates,
				useApproximation ? 120 : 60,
				numWorkers,
				(progressResults) => {
					accumulatedResultsRef.current.push(...progressResults);
					updateSunlightVisualization(accumulatedResultsRef.current);
					const percentage = (
						(accumulatedResultsRef.current.length / allGridPoints.length) *
						100
					).toFixed(1);
					
					const etaResult = calculateETA(
						accumulatedResultsRef.current.length,
						allGridPoints.length,
						startTime,
						lastUpdateTime,
						lastEtaText
					);
					
					lastUpdateTime = etaResult.newLastUpdateTime;
					lastEtaText = etaResult.etaText;
					
					setCurrentStatusMessage(
						`Progress: ${accumulatedResultsRef.current.length}/${allGridPoints.length} (${percentage}%)${etaResult.etaText}`
					);
				},
				(completeResults) => {
					accumulatedResultsRef.current = completeResults;
					updateSunlightVisualization(completeResults);
					setCalculatingDailySunlight(false);
					setCurrentStatusMessage('');
				}
			);
			return accumulatedResultsRef.current;
		} catch (error) {
			console.error('Error during room sunlight calculation:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingDailySunlight(false);
			setCurrentStatusMessage('');
		}
	};

	const calculateDailySunlightForSelectedGridPoints = async (): Promise<
		DailySunlightResult[] | undefined
	> => {
		const selectedCells = getSelectedGridPoints();

		if (selectedCells.length === 0) {
			console.warn('No grid points selected');
			return;
		}

		if (!sceneRef.current || latitude === null || longitude === null) {
			console.error('Scene or coordinates not available');
			return;
		}

		setCalculatingDailySunlight(true);
		accumulatedRegionResultsRef.current = [];

		const analysisStart = useDailySunlightStore.getState().startDate;
		const analysisEnd = useDailySunlightStore.getState().endDate;
		const regionNumber =
			useDailySunlightStore.getState().sunlightResultsGridPoints?.length || 0;

		const useApproximation = false;
		const cellSize = 0.2;

		if (!analysisStart || !analysisEnd) {
			console.error('Date range not configured');
			setCalculatingDailySunlight(false);
			return;
		}

		try {
			setCurrentStatusMessage('Extracting selected grid points...');

			const boundaryPolygon = createPolygonFromGridPoints(
				selectedCells,
				useDailySunlightStore.getState().gridSize
			);

			setCurrentStatusMessage('Generating interior grid points...');

			const interiorPoints = fillPolygonWithGrid(boundaryPolygon, cellSize);

			if (interiorPoints.length === 0) {
				console.warn('No interior points generated');
				setCalculatingDailySunlight(false);
				return;
			}

			const shadowGeometry = getShadowCastersForWorker(sceneRef.current);
			const analysisDates = buildDateSequence(analysisStart, analysisEnd, useApproximation);

			initializeSelectedRegionVisualization(
				sceneRef.current,
				interiorPoints.length,
				cellSize
			);

			const roomNames = Array(interiorPoints.length).fill(`Selected Region ${regionNumber}`);
			const roomUnitIdMap = new Map<string, string | undefined>();
			roomUnitIdMap.set(`Selected Region ${regionNumber}`, undefined);

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);

			setCurrentStatusMessage(
				`Calculating sunlight for ${interiorPoints.length} selected points...`
			);

			await runSunlightWorkers(
				interiorPoints,
				roomNames,
				roomUnitIdMap,
				shadowGeometry,
				analysisDates,
				60,
				numWorkers,
				(progressResults, completed, total) => {
					accumulatedRegionResultsRef.current.push(...progressResults);
					updateSelectedRegionVisualization(accumulatedRegionResultsRef.current);
					const percentage = (
						(accumulatedRegionResultsRef.current.length / interiorPoints.length) *
						100
					).toFixed(1);
					setCurrentStatusMessage(
						`Progress: ${accumulatedRegionResultsRef.current.length}/${interiorPoints.length} (${percentage}%) - ${completed}/${total} workers completed`
					);
				},
				(completeResults) => {
					accumulatedRegionResultsRef.current = completeResults;
					updateSelectedRegionVisualization(completeResults);
					setCalculatingDailySunlight(false);
					setCurrentStatusMessage('');
					clearAllSelections(sceneRef.current!);
					persistPolygonFill(sceneRef.current!);
				}
			);

			return accumulatedRegionResultsRef.current;
		} catch (error) {
			console.error('Failed to analyze selected region:', error);
			setCalculatingDailySunlight(false);
			setCurrentStatusMessage('');
		}
	};

	const fillPolygonWithGrid = (
		boundary: Array<{ x: number; y: number; z: number }>,
		spacing: number
	): Array<{ x: number; y: number; z: number }> => {
		if (boundary.length < 3) return [];

		const xValues = boundary.map((p) => p.x);
		const yValues = boundary.map((p) => p.y);
		const bounds = {
			minX: Math.min(...xValues),
			maxX: Math.max(...xValues),
			minY: Math.min(...yValues),
			maxY: Math.max(...yValues),
			z: boundary[0].z, // Z-up: use boundary elevation
		};

		const gridPoints: Array<{ x: number; y: number; z: number }> = [];

		for (
			let xPos = bounds.minX + spacing / 2;
			xPos <= bounds.maxX - spacing / 2;
			xPos += spacing
		) {
			for (
				let yPos = bounds.minY + spacing / 2;
				yPos <= bounds.maxY - spacing / 2;
				yPos += spacing
			) {
				if (testPointInBoundary({ x: xPos, y: yPos }, boundary)) {
					gridPoints.push({ x: xPos, y: yPos, z: bounds.z });
				}
			}
		}

		return gridPoints;
	};

	const changeAllRegionsVisualizationMetric = (metric: keyof DailySunlightResult) => {
		const regions: THREE.Object3D[] = [];
		sceneRef.current?.traverse((child) => {
			if (child.userData.isSelectedRegionVisualization) {
				regions.push(child);
			}
		});

		regions.forEach((region) => {
			changeSelectedRegionVisualizationMetric(
				region.userData.gridPointsData,
				metric,
				region as THREE.InstancedMesh
			);
		});
	};

	const testPointInBoundary = (
		testPoint: { x: number; y: number },
		boundary: Array<{ x: number; y: number; z: number }>
	): boolean => {
		let crossings = 0;
		const vertexCount = boundary.length;

		for (let i = 0; i < vertexCount; i++) {
			const current = boundary[i];
			const next = boundary[(i + 1) % vertexCount];

			const yCheck = current.y > testPoint.y !== next.y > testPoint.y;
			const xIntercept =
				((next.x - current.x) * (testPoint.y - current.y)) / (next.y - current.y) +
				current.x;

			if (yCheck && testPoint.x < xIntercept) {
				crossings++;
			}
		}

		return crossings % 2 === 1;
	};

	const stopDailyAnimation = useCallback((): void => {
		animationControlRef.current.shouldStop = true;
		setIsAnimatingDaily(false);
		setCurrentStatusMessage('');
	}, [setCurrentStatusMessage]);

	const startDailyAnimation = useCallback(async (): Promise<void> => {
		if (!sceneRef.current || latitude === null || longitude === null || isAnimating) {
			return;
		}

		setIsAnimatingDaily(true);
		animationControlRef.current.shouldStop = false;
		setCalculatingDailySunlight(true);

		try {
			const startDate = useDailySunlightStore.getState().startDate;
			const endDate = useDailySunlightStore.getState().endDate;
			if (!startDate || !endDate) {
				console.error('Start date or end date is not set');
				return;
			}

			const useApproximation = useDailySunlightStore.getState().useApproximation;
			const analysisDates = buildDateSequence(startDate, endDate, useApproximation);
			const totalDays = analysisDates.length;

			// Get rooms data
			const rooms = sceneRef.current.getObjectByName('RoomFloorPolygonsGroup');
			if (!rooms) {
				console.error('Rooms object not found');
				return;
			}

			const shadowCasters = getShadowCastersForWorker(sceneRef.current);
			const gridSize = useDailySunlightStore.getState().gridSize;
			const { allGridPoints, roomNames, roomUnitIdMap } = generateRoomGridPoints(rooms, gridSize);

			// Prepare grid points for all rooms
			if (allGridPoints.length === 0) {
				console.warn('No grid points to analyze');
				return;
			}

		// Check if grid size has changed (different number of points)
		// If so, clear old results and reinitialize
		if (accumulatedResultsRef.current.length > 0 && accumulatedResultsRef.current.length !== allGridPoints.length) {
			accumulatedResultsRef.current = [];
			// Reset to day 0 when grid size changes
			setCurrentDailyAnimationDay(0);
		}

		// Initialize visualization if empty or grid size changed
		if (accumulatedResultsRef.current.length === 0) {
			initializeSunlightVisualization(sceneRef.current, allGridPoints.length, gridSize);
		}

		const numCores = navigator.hardwareConcurrency || 4;
		const numWorkers = Math.min(Math.max(1, numCores - 1), 8);

		// Start from current day if resuming (or 0 if grid size changed)
		const startDayIndex = useDailySunlightStore.getState().currentDailyAnimationDay || 0;

		// Animate through all analysis dates
		for (let dayIndex = startDayIndex; dayIndex < totalDays; dayIndex++) {
			if (animationControlRef.current.shouldStop) {
				break;
			}

			const dateStr = analysisDates[dayIndex];

			const dayStartTime = Date.now();
			let dayLastUpdateTime = dayStartTime;
			let dayLastEtaText = '';
		const dayAccumulatedResults: typeof accumulatedResultsRef.current = [];

		// Calculate sunlight for this single day
		await runSunlightWorkers(
			allGridPoints,
			roomNames,
			roomUnitIdMap,
			shadowCasters,
			[dateStr],
			useApproximation ? 120 : 60,
			numWorkers,
			(progressResults) => {
				dayAccumulatedResults.push(...progressResults);
				const processedCount = dayAccumulatedResults.length;
				const percentage = ((processedCount / allGridPoints.length) * 100).toFixed(1);
				
				const etaResult = calculateETA(
					processedCount,
					allGridPoints.length,
					dayStartTime,
					dayLastUpdateTime,
					dayLastEtaText
				);
				
				dayLastUpdateTime = etaResult.newLastUpdateTime;
				dayLastEtaText = etaResult.etaText;
				
				setCurrentStatusMessage(
					`Animating day ${dayIndex + 1}/${totalDays}: ${new Date(dateStr).toDateString()} - Progress: ${processedCount}/${allGridPoints.length} (${percentage}%)${etaResult.etaText}`
				);
			},
			(completeResults) => {
				accumulatedResultsRef.current = completeResults;
				updateSunlightVisualization(completeResults);
			}
		);

			// Update day counter after calculation completes
			setCurrentDailyAnimationDay(dayIndex);
		}
		} catch (error) {
			console.error('Error in daily animation:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingDailySunlight(false);
			setIsAnimatingDaily(false);
		}
	}, [sceneRef, latitude, longitude, isAnimating, setCurrentStatusMessage]);

	const calculateSingleDay = useCallback(async (dayIndex: number): Promise<void> => {
		if (!sceneRef.current || latitude === null || longitude === null) {
			return;
		}

		setCalculatingDailySunlight(true);

		try {
			const startDate = useDailySunlightStore.getState().startDate;
			const endDate = useDailySunlightStore.getState().endDate;
			if (!startDate || !endDate) {
				console.error('Start date or end date is not set');
				return;
			}

			const useApproximation = useDailySunlightStore.getState().useApproximation;
			const analysisDates = buildDateSequence(startDate, endDate, useApproximation);

			if (dayIndex < 0 || dayIndex >= analysisDates.length) {
				console.error('Invalid day index');
				return;
			}

			const dateStr = analysisDates[dayIndex];

			// Get rooms data
			const rooms = sceneRef.current.getObjectByName('RoomFloorPolygonsGroup');
			if (!rooms) {
				console.error('Rooms object not found');
				return;
			}

			const shadowCasters = getShadowCastersForWorker(sceneRef.current);
			const gridSize = useDailySunlightStore.getState().gridSize;
			const { allGridPoints, roomNames, roomUnitIdMap } = generateRoomGridPoints(rooms, gridSize);

			// Initialize visualization if empty
			if (accumulatedResultsRef.current.length === 0) {
				initializeSunlightVisualization(sceneRef.current, allGridPoints.length, gridSize);
			}

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);

			setCurrentStatusMessage(`Calculating day ${dayIndex + 1}/${analysisDates.length}: ${dateStr}`);

			await runSunlightWorkers(
				allGridPoints,
				roomNames,
				roomUnitIdMap,
				shadowCasters,
				[dateStr],
				useApproximation ? 120 : 60,
				numWorkers,
				() => {},
				(completeResults) => {
					accumulatedResultsRef.current = completeResults;
					updateSunlightVisualization(completeResults);
				}
			);

			setCurrentStatusMessage('');
		} catch (error) {
			console.error('Error calculating single day sunlight:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingDailySunlight(false);
		}
	}, [sceneRef, latitude, longitude, setCurrentStatusMessage]);

	return {
		calculatingDailySunlight,
		calculateDailySunlightForRooms,
		calculateDailySunlightForSelectedGridPoints,
		changeSunlightVisualizationMetric: (metric: keyof DailySunlightResult) => {
			if (!sceneRef.current) return;
			changeSunlightVisualizationMetric(accumulatedResultsRef.current, metric);
			changeAllRegionsVisualizationMetric(metric);
		},
		clearAllVisualizations: () => {
			if (!sceneRef.current) return;
			clearAllVisualizations(sceneRef.current);
			// Clear accumulated results so animation can reinitialize
			accumulatedResultsRef.current = [];
		},
		startDailyAnimation,
		stopDailyAnimation,
		isAnimating,
		calculateSingleDay,
	};
};
